import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axios';
import { ArrowLeft, Calendar, X, Clock } from 'lucide-react';
import { format } from 'date-fns';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const PAGE_SIZE = 20;

const fmtDate = (d) => format(new Date(`${d}T00:00:00`), 'dd-MM-yy');
const toYmd = (d) => format(d, 'yyyy-MM-dd');
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
};

export default function ChecklistDailyStatusDetailPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const dpRef = useRef(null);

  const [staff, setStaff] = useState(null);
  const [history, setHistory] = useState({ totalItems: 0, days: [] });
  const [loading, setLoading] = useState(true);

  const [range, setRange] = useState([null, null]);
  const [rangeStart, rangeEnd] = range;
  const [applied, setApplied] = useState({ from: '', to: '' });

  const [page, setPage] = useState(1);
  const [viewDate, setViewDate] = useState(null); // 'yyyy-MM-dd' of the day popup
  const [day, setDay] = useState(null); // { checklist, slots }
  const [dayLoading, setDayLoading] = useState(false);

  useEffect(() => {
    api.get(`/staff/${staffId}`).then((r) => setStaff(r.data)).catch(() => setStaff(null));
  }, [staffId]);

  useEffect(() => {
    setLoading(true);
    const params = applied.from && applied.to
      ? `?from=${applied.from}&to=${applied.to}`
      : '';
    api.get(`/staff-checklists/${staffId}/history${params}`)
      .then((r) => setHistory(r.data || { totalItems: 0, days: [] }))
      .catch(() => setHistory({ totalItems: 0, days: [] }))
      .finally(() => setLoading(false));
  }, [staffId, applied]);

  const openDay = (dateStr) => {
    setViewDate(dateStr);
    setDay(null);
    setDayLoading(true);
    api.get(`/staff-checklists/${staffId}/day?date=${dateStr}`)
      .then((r) => setDay(r.data || { checklist: null, slots: [] }))
      .catch(() => setDay({ checklist: null, slots: [] }))
      .finally(() => setDayLoading(false));
  };

  const closeDay = () => { setViewDate(null); setDay(null); };

  const totalPages = Math.ceil(history.days.length / PAGE_SIZE);
  const pageDays = history.days.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [applied, staffId]);

  const applyRange = () => {
    if (rangeStart && rangeEnd) {
      setApplied({ from: toYmd(rangeStart), to: toYmd(rangeEnd) });
    }
    dpRef.current?.setOpen(false);
  };

  if (!staff) return <div className="page"><div className="empty-state">Loading...</div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="staff-header-card" style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <button className="back-arrow-btn" onClick={() => navigate('/checklist-daily-status')} title="Back">
            <ArrowLeft size={22} />
          </button>
          {staff.photo && <img src={`${API_BASE}/uploads/${staff.photo}`} alt="" className="staff-photo-sm" />}
          <div>
            <h2 style={{ fontSize: 20 }}>{staff.name}</h2>
            <p className="text-muted">{staff.staffId} · {staff.outlet?.name}</p>
          </div>
        </div>
        <div className="header-actions">
          <DatePicker
            ref={dpRef}
            showIcon
            icon={<Calendar size={16} />}
            selectsRange
            startDate={rangeStart}
            endDate={rangeEnd}
            onChange={(update) => {
              const u = update || [null, null];
              setRange(u);
              if (!u[0] && !u[1]) setApplied({ from: '', to: '' });
            }}
            shouldCloseOnSelect={false}
            isClearable
            dateFormat="dd MMM yyyy"
            placeholderText="Select date range"
            className="form-input datepicker-input logs-datepicker"
            popperPlacement="bottom-end"
            popperProps={{ strategy: 'fixed' }}
          >
            <div className="datepicker-footer">
              <button type="button" className="btn btn-sm btn-primary" onClick={applyRange}>OK</button>
            </div>
          </DatePicker>
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {!loading && pageDays.map((d) => (
              <tr key={d.date}>
                <td>{fmtDate(d.date)}</td>
                <td>
                  {d.total === 0
                    ? '—'
                    : (
                      <button className="btn-link" onClick={() => openDay(d.date)}>
                        Complete - {String(d.done).padStart(2, '0')}/{String(d.total).padStart(2, '0')}
                      </button>
                    )}
                </td>
              </tr>
            ))}
            {!loading && history.days.length === 0 && (
              <tr><td colSpan={2} className="empty-state">No checklist assigned to {staff.name}.</td></tr>
            )}
            {loading && <tr><td colSpan={2} className="empty-state">Loading...</td></tr>}
          </tbody>
        </table>
      </div>

      {history.days.length > PAGE_SIZE && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, history.days.length)}–{Math.min(page * PAGE_SIZE, history.days.length)} of {history.days.length}
          </span>
          <div className="pagination-controls">
            <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, idx) => p === '...'
                ? <span key={`e${idx}`} className="pagination-ellipsis">…</span>
                : <button key={p} className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>Next ›</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        </div>
      )}

      {/* Day detail popup */}
      {viewDate && (
        <div className="modal-overlay">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>{staff.name} — Checklist Status</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={closeDay}><X size={16} /></button>
            </div>

            {dayLoading && <div className="empty-state">Loading...</div>}

            {!dayLoading && day && day.slots.length === 0 && (
              <div className="empty-state">No checklist tasks for this day.</div>
            )}

            {!dayLoading && day && day.slots.length > 0 && (
              <>
                <div className="text-muted" style={{ marginBottom: 12 }}>
                  <strong>{format(new Date(`${viewDate}T00:00:00`), 'dd MMM yyyy')}</strong>
                  {day.checklist ? ` · ${day.checklist.name}` : ''}
                  {' · '}
                  {day.slots.reduce((n, s) => n + s.items.filter((i) => i.completed).length, 0)} of{' '}
                  {day.slots.reduce((n, s) => n + s.items.length, 0)} completed
                </div>

                {day.slots.map((s) => (
                  <div key={s._id} className="slot-block">
                    <div className="slot-head">
                      <Clock size={14} />
                      <span>
                        {s.items.length} task{s.items.length === 1 ? '' : 's'} ·{' '}
                        {s.from ? fmt12(s.from) : ''}{s.from && s.to ? ' – ' : ''}{s.to ? fmt12(s.to) : ''}
                        {(s.from || s.to) && s.label ? ' · ' : ''}{s.label}
                      </span>
                    </div>
                    <div className="checklist-list">
                      {s.items.map((it) => (
                        <div key={it.slotItem} className={`checklist-row ${it.completed ? 'done' : ''}`}>
                          <label className="checklist-check" style={{ cursor: 'default' }}>
                            <input type="checkbox" checked={it.completed} readOnly disabled />
                            <span className="checklist-text">
                              <span className="checklist-title">{it.title}</span>
                              {it.description && <span className="checklist-desc">{it.description}</span>}
                            </span>
                          </label>
                          {it.completed && it.completedAt && <span className="checklist-time">✓ {fmtTime(it.completedAt)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
